import type { RentalInfo } from "@/types/rental"
import type { HttpTypes } from "@medusajs/types"

/**
 * Build a lightweight RentalInfo from the rental_price_summary
 * that the middleware injects into /store/products responses.
 *
 * This avoids N individual /rental-info requests on listing pages.
 * Full RentalInfo (with deposit, policies, etc.) is still fetched
 * on the product detail page.
 */
export function rentalInfoFromSummary(
  product: HttpTypes.StoreProduct,
  currencyCode: string
): RentalInfo | null {
  const summary = (product as any).rental_price_summary
  if (!summary) return null

  const isRentalAvailable =
    summary.availability_mode === "rental_only" ||
    summary.availability_mode === "both"

  if (!isRentalAvailable) return null

  // A rental-ONLY product must hide the "buy" button in the catalog even when
  // this lightweight listing summary has no price (custom / no-template
  // pricing → price_per_unit null). Previously a null price discarded the whole
  // summary, so the card fell back to showing "buy" — the reported catalog bug.
  // For 'both' we still require a price to justify offering rent on the card;
  // without one, fall back to the sale button.
  if (summary.price_per_unit == null && summary.availability_mode !== "rental_only") {
    return null
  }

  // Prefer the rental template's currency (the price was authored in that
  // currency); fall back to the active region's currency if the template
  // didn't report one. This prevents a CZK rental price being displayed as USD.
  const effectiveCurrency = (summary.currency_code as string | null) || currencyCode

  return {
    productId: product.id,
    isRentalAvailable: true,
    availabilityMode: summary.availability_mode,
    salePrice: null,
    rentalPrice: summary.price_per_unit,
    depositAmount: summary.deposit_amount ?? null,
    rentalUnit: summary.rental_unit || "month",
    minDuration: 1,
    maxDuration: null,
    gracePeriodHours: 0,
    lateFeePerDay: null,
    allowExtensions: false,
    maxExtensions: null,
    requiresDeposit: false,
    currencyCode: effectiveCurrency.toUpperCase(),
  }
}
